import React, { useState, useEffect } from "react";
import { Admin_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaTrash, FaPlus, FaFilter } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";

// Component for managing admins with add, delete, multiple delete, and filter functionality
export default function Admins() {
  const { userData } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    id: [],
    username: [],
    email: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const columnDisplay = {
    id: "Admin ID",
    username: "Username",
    email: "Email",
  };


  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = admins.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort(
      (a, b) =>
        column === "id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.id.length === 0 || filters.id.includes(item.id)) &&
      (filters.username.length === 0 || filters.username.includes(item.username)) &&
      (filters.email.length === 0 || filters.email.includes(item.email))
    );
  };

  // Filter admins
  const filteredAdmins = admins.filter(applyFilters);

  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);
  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Fetch all admins on component mount
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await fetch(`${Admin_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch admins");
        }
        const data = await response.json();
        setAdmins(data);
      } catch (error) {
        console.error("Error fetching admins:", error);
        Swal.fire("Error", "Failed to fetch admins. Please try again.", "error");
      }
    };
    fetchAdmins();
  }, [userData.access_token]);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered admins
  const handleSelectAll = () => {
    if (selectedRows.length === filteredAdmins.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredAdmins.map((item) => item.id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected admins?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (id) => {
              const response = await fetch(`${Admin_API}/delete/${id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error("Server Error:", errorText);
                throw new Error(`Failed to delete admin ${id}`);
              }
            })
          );

          const updatedAdmins = admins.filter(
            (admin) => !selectedRows.includes(admin.id)
          );
          setAdmins(updatedAdmins);
          setSelectedRows([]);

          Swal.fire(
            "Success!",
            "Selected admins deleted successfully",
            "success"
          );
        } catch (error) {
          console.error("Error deleting admins:", error);
          Swal.fire(
            "Error",
            "Failed to delete some admins. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Function to add a new admin using SweetAlert for input
  const addAdmin = async () => {
    const { value } = await Swal.fire({
      title: "Create new admin",
      html:
        '<input id="swal-username" class="swal2-input" placeholder="Enter username">' +
        '<input id="swal-email" class="swal2-input" placeholder="Enter email">' +
        '<input id="swal-password" class="swal2-input" type="password" placeholder="Enter password">',
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const username = document.getElementById("swal-username").value;
        const email = document.getElementById("swal-email").value;
        const password = document.getElementById("swal-password").value;
        if (!username || !email || !password) {
          Swal.showValidationMessage("All fields are required!");
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
          Swal.showValidationMessage("Please enter a valid email!");
          return false;
        }
        return { username, email, password };
      },
    });

    if (value) {
      try {
        const response = await fetch(`${Admin_API}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            username: value.username,
            email: value.email,
            password: value.password,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to add admin");
        }

        const newAdmin = await response.json();
        setAdmins([...admins, newAdmin]);
        Swal.fire("Success!", "Admin added successfully.", "success");
      } catch (error) {
        console.error("Error adding admin:", error);
        Swal.fire("Error", "Failed to add admin. Please try again.", "error");
      }
    }
  };

  // Function to delete a single admin with confirmation
  const deleteAdmin = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${Admin_API}/delete/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete admin");
        }

        setAdmins(admins.filter((admin) => admin.id !== id));
        Swal.fire("Deleted!", "Admin has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting admin:", error);
        Swal.fire("Error", "Failed to delete admin. Please try again.", "error");
      }
    }
  };

  // Render filter popup for a column
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null &&
        val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(
          tempSelected.filter((val) => !filteredValues.includes(val))
        );
      }
    };

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div
        className="filter-popup"
        style={{
          position: "absolute",
          top: "100%",
          left: "0",
          zIndex: 100,
        }}
      >
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTempSelected([...tempSelected, val]);
                    } else {
                      setTempSelected(
                        tempSelected.filter((v) => v !== val)
                      );
                    }
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Admins - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>Admins</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredAdmins.length &&
                    filteredAdmins.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredAdmins.length &&
                      filteredAdmins.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                Admin ID
                <FaFilter
                  className={`filter-icon ${
                    filters.id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "id" && renderFilterPopup("id")}
              </th>
              <th style={{ position: "relative" }}>
                Username
                <FaFilter
                  className={`filter-icon ${
                    filters.username.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("username")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "username" && renderFilterPopup("username")}
              </th>
              <th style={{ position: "relative" }}>
                Email
                <FaFilter
                  className={`filter-icon ${
                    filters.email.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("email")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "email" && renderFilterPopup("email")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAdmins.map((admin) => (
              <tr
                key={admin.id}
                className={selectedRows.includes(admin.id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(admin.id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(admin.id)}
                    onChange={() => toggleRowSelection(admin.id)}
                  />
                </td>
                <td>{admin.id}</td>
                <td>{admin.username}</td>
                <td>{admin.email}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteAdmin(admin.id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addAdmin}>
        <FaPlus />
      </button>
    </div>
  );
}